import React, { memo, useState, useEffect, useRef } from "react";
import { useIsOverviewMode } from "../../hooks/useIsOverviewMode";
import { useGoogleAnalytics } from "../../hooks/useGoogleAnalytics";
import { IVerticalScrollableRef, SpreadOverview } from "./spread-overview";
import { useDashboardContext } from "../../contexts/dashboard-context";
import { useSpreads } from "../../hooks/useSpreads";
import { useEditor } from "../../hooks/useEditor";

export const SpreadOverviewContainer = memo(() => {
	const editor = useEditor();
	const { selection } = editor;

	const [isOpen, setOpen] = useIsOverviewMode();
	const [selectedSpreadIndex, setSelectedSpreadIndex] = useState<number>();

	const contentContainerRef = useRef<IVerticalScrollableRef>(null);

	// Retrigger a rerender on spreads changes to update disabled state of delete button
	const spreads = useSpreads();
	const dashboard = useDashboardContext();
	const googleAnalytics = useGoogleAnalytics();

	const scrollIndexIntoView = (index: number, animate = false) => {
		if (contentContainerRef.current) {
			contentContainerRef.current.scrollIntoView(index, animate);
		}
	};

	// Updates spread selection when spreads array changes
	// This is needed because our selection is not in sync with the core selection.
	// If the core selection is already on the spread that must be selected after a delete, we don't get a rerender from useSelectedSpread
	useEffect(
		function updateSpreadSelection() {
			if (isOpen) {
				const index = selection.getCurrentSpread()?.index ?? 0;
				setSelectedSpreadIndex(index);
				scrollIndexIntoView(index, true);
			}
		},
		[selection, isOpen, spreads],
	);

	// When screen is shown, we don't want animation
	useEffect(
		function scrollOnMount() {
			if (isOpen) {
				const index = selection.getCurrentSpread()?.index ?? 0;
				scrollIndexIntoView(index, false);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isOpen],
	);

	// Local Spread change, not core selection
	const handleSelectIndex = (index: number) => {
		setSelectedSpreadIndex(index);
		scrollIndexIntoView(index, true);
	};

	const handleClose = () => {
		if (selectedSpreadIndex !== undefined) {
			const overviewSpread = spreads[selectedSpreadIndex];

			// set selected spread in preview
			selection.setCurrentSpreadAndBroadCast(overviewSpread);
		}

		googleAnalytics.trackEventToGa({
			trackingAction: "OverviewClosed",
		});
		setOpen(false);
	};

	const handleDeleteSelection = () => {
		if (selectedSpreadIndex === undefined) return;

		const overviewSpread = spreads[selectedSpreadIndex];
		if (!overviewSpread) return;

		// First set current spread in core, so next spread to select in message is OK (based on the core selection, not overview spread selection)
		// no broadcast needed, because after remove a broadcast will happen)
		selection.setCurrentSpread(overviewSpread);
		dashboard.removeSpread(selectedSpreadIndex);
	};

	const isDeleteEnabled =
		selectedSpreadIndex !== undefined && dashboard.canRemoveSpread(selectedSpreadIndex);

	if (!isOpen) return null;

	return (
		<SpreadOverview
			onClose={handleClose}
			selectedSpreadIndex={selectedSpreadIndex}
			onSelectSpreadIndex={handleSelectIndex}
			onDeleteSelection={handleDeleteSelection}
			isDeleteEnabled={isDeleteEnabled}
			contentScrollRef={contentContainerRef}
		/>
	);
});

SpreadOverviewContainer.displayName = "SpreadOverviewContainer";
